// BUILD-046 correction cycle 1bb88b3e (stage B1, item N1): the sorting and quick filters of My Work, moved here VERBATIM from MyWorkPage.tsx so the Shared and
// Team views use the same, already-working sort (Builder decision: "preserve sorting reported working; no speculative sort rewrite").
//
// The five declarations below (PRIORITY_ORDER, isOverdue, isDueToday, sortWorkItems, filterWorkItems) are the page's own text, character for character. That
// matters because test/shared-tasks/page-logic-regression.test.mjs (task 9.4, Req 1.3, 9.29, 9.30) hashes exactly this text and replays its results under
// 3 time zones x 3 instants against the golden captured on the unmodified base; the test now reads them from THIS file, with the same hashes and the same
// fixture results, and also asserts that the page imports them instead of declaring its own. Nothing in the declarations may be edited without that golden
// being re-captured on purpose.
//
// Below the verbatim part: the generic wrappers the other views use (`sortItems`, `filterItems`: the same functions over any task with the few fields they
// read), the mode list and labels (as the existing select shows them), and which modes a view offers. `sortModel.test.mjs` also pins the order every mode
// produces on a fixed fixture.
//
// Erasable TypeScript only (declarations at the start of a line, no `export` prefix, so the regression test's extraction rules still apply).

import type { WorkItem } from '../../api/client';

export type SortMode = 'recommended' | 'priority' | 'due-soonest' | 'due-latest' | 'oldest' | 'newest' | 'project';
export type QuickFilter = 'all' | 'urgent' | 'high' | 'due-today' | 'overdue' | 'no-due';

// ───── verbatim from MyWorkPage.tsx (hashed by page-logic-regression.test.mjs) ─────
const PRIORITY_ORDER: Record<string, number> = { urgent: 4, high: 3, medium: 2, low: 1 };
function isOverdue(item: WorkItem) { return item.dueDate && item.status !== 'completed' && new Date(item.dueDate) < new Date(new Date().toDateString()); }
function isDueToday(item: WorkItem) { return item.dueDate && new Date(item.dueDate).toDateString() === new Date().toDateString(); }

function sortWorkItems(items: WorkItem[], mode: SortMode): WorkItem[] {
  const s = [...items];
  switch (mode) {
    case 'priority': return s.sort((a, b) => (PRIORITY_ORDER[b.priority || 'low'] || 0) - (PRIORITY_ORDER[a.priority || 'low'] || 0));
    case 'due-soonest': return s.sort((a, b) => (a.dueDate || '9999').localeCompare(b.dueDate || '9999'));
    case 'due-latest': return s.sort((a, b) => (b.dueDate || '').localeCompare(a.dueDate || ''));
    case 'oldest': return s.sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
    case 'newest': return s.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
    case 'project': return s.sort((a, b) => (a.projectDisplayName || '~').localeCompare(b.projectDisplayName || '~'));
    default: // recommended: overdue > urgent/high due soon > due today > by priority > rest
      return s.sort((a, b) => {
        const ao = isOverdue(a) ? 0 : isDueToday(a) ? 1 : a.dueDate ? 2 : 3;
        const bo = isOverdue(b) ? 0 : isDueToday(b) ? 1 : b.dueDate ? 2 : 3;
        if (ao !== bo) return ao - bo;
        return (PRIORITY_ORDER[b.priority || 'low'] || 0) - (PRIORITY_ORDER[a.priority || 'low'] || 0);
      });
  }
}
function filterWorkItems(items: WorkItem[], f: QuickFilter): WorkItem[] {
  switch (f) {
    case 'urgent': return items.filter((i) => i.priority === 'urgent');
    case 'high': return items.filter((i) => i.priority === 'high' || i.priority === 'urgent');
    case 'due-today': return items.filter((i) => isDueToday(i));
    case 'overdue': return items.filter((i) => isOverdue(i));
    case 'no-due': return items.filter((i) => !i.dueDate);
    default: return items;
  }
}
// ───── end of the verbatim part ─────

export { PRIORITY_ORDER, isOverdue, isDueToday, sortWorkItems, filterWorkItems };

/** The fields the sort and the filters read. Both the legacy WorkItem and the WorkTask of the new routes have them. */
export interface SortableItem {
  status: string;
  priority?: string;
  dueDate?: string;
  createdAt?: string;
  projectDisplayName?: string;
}

/** `sortWorkItems` over any task with the fields it reads (a WorkTask of the oversight or shared list needs no cast at the call site). */
export function sortItems<T extends SortableItem>(items: T[], mode: SortMode): T[] {
  return sortWorkItems(items as unknown as WorkItem[], mode) as unknown as T[];
}

/** `filterWorkItems` over any task with the fields it reads. */
export function filterItems<T extends SortableItem>(items: T[], filter: QuickFilter): T[] {
  return filterWorkItems(items as unknown as WorkItem[], filter) as unknown as T[];
}

/** Whether the page counts the task as overdue (the page's own rule: a past due date on a task that is not completed). */
export function itemIsOverdue(item: SortableItem): boolean {
  return Boolean(isOverdue(item as unknown as WorkItem));
}

// ───────────────────────── which modes a view offers ─────────────────────────

export const SORT_LABELS: Readonly<Record<SortMode, string>> = Object.freeze({
  recommended: 'Recommended',
  priority: 'Priority',
  'due-soonest': 'Due Soonest',
  'due-latest': 'Due Latest',
  oldest: 'Oldest',
  newest: 'Newest',
  project: 'Project',
});

/** All seven modes, in the order the existing select shows them. */
export const SORT_MODES: readonly SortMode[] = Object.freeze(['recommended', 'priority', 'due-soonest', 'due-latest', 'oldest', 'newest', 'project'] as SortMode[]);

export const DEFAULT_SORT: SortMode = 'recommended';

export function isSortMode(value: unknown): value is SortMode {
  return typeof value === 'string' && (SORT_MODES as readonly string[]).includes(value);
}

export type SortView = 'my-work' | 'group-shared' | 'team-work';

/**
 * The modes the view offers, in display order. My Work: all seven, always (the control it already has). Shared and Team: the six that mean something for
 * every task, plus Project only when a loaded task carries a project (otherwise it would sort nothing).
 */
export function applicableSortModes(view: SortView, loaded: readonly { projectDisplayName?: unknown }[] = []): SortMode[] {
  if (view === 'my-work') return [...SORT_MODES];
  const withProject = loaded.some((item) => typeof item.projectDisplayName === 'string' && item.projectDisplayName.trim() !== '');
  return SORT_MODES.filter((mode) => mode !== 'project' || withProject);
}

/** The mode to apply in a view: the chosen one when it is offered there, else Recommended. The stored choice is not overwritten by this. */
export function effectiveSortMode(chosen: unknown, view: SortView, loaded: readonly { projectDisplayName?: unknown }[] = []): SortMode {
  return isSortMode(chosen) && applicableSortModes(view, loaded).includes(chosen) ? chosen : DEFAULT_SORT;
}
